import React, { memo, useMemo, useRef, useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Entypo, Ionicons } from "@expo/vector-icons";
import { useDraggable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import ActivePulseIndicator from "../../../Block/shared/ActivePulseIndicator";
import FloatingMenu from "../../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import { useDevice, useScreenDimensions } from "../../../_layout";
import {
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "../../../Firestore/nutrition/nutritionPaths";
import NutritionConflictWarningIcon from "../../shared/NutritionConflictWarningIcon";
import { getNutritionConflictDescription } from "../../shared/nutritionConflictWarnings";
import { formatNutritionPlanUpdatedDate } from "../nutritionPlanDateUtils";
import { getNutritionPlanDisplayName } from "../nutritionPlanNameUtils";
import {
  formatCalorieValue,
  formatMacroValue,
  getUiMacroShortLabel,
  UI_MACRO_DISPLAY_ORDER,
} from "../../shared/nutritionFormatters";
import { resolveClientNutritionPlanRowTotals } from "../clientNutritionPlanListUtils";

const getCardWidthStyle = (screenSize) => {
  if (screenSize === "small") return styles.cardSmall;
  if (screenSize === "medium") return styles.cardMedium;
  return styles.cardLarge;
};

const styles = StyleSheet.create({
  cardWrapDragging: {
    opacity: 0.48,
  },
  cardSmall: {
    width: "100%",
  },
  cardMedium: {
    flexBasis: "calc((100% - 12px) / 2)",
    flexGrow: 0,
    flexShrink: 0,
  },
  cardLarge: {
    flexBasis: "calc((100% - 24px) / 3)",
    flexGrow: 0,
    flexShrink: 0,
  },
  rowCard: {
    backgroundColor: Colors.whiteColor,
    borderColor: Colors.borderColor,
    borderRadius: 12,
    borderWidth: 0.1,
    overflow: "hidden",
    transitionDuration: "200ms",
    transitionProperty: "border-color",
    transitionTimingFunction: "ease-in-out",
  },
  rowTouchable: {
    boxShadow: "none",
    outlineColor: "transparent",
    outlineStyle: "none",
    outlineWidth: 0,
    width: "100%",
  },
  rowCardHovered: {
    borderColor: Colors.grayColor,
  },
  rowCardDropZoneActive: {
    opacity: 0.55,
  },
  rowContent: {
    gap: 8,
    paddingBottom: 14,
    paddingHorizontal: 14,
    paddingTop: 12,
    position: "relative",
  },
  rowHeader: {
    alignItems: "flex-start",
    flexDirection: "row",
    gap: 12,
    justifyContent: "space-between",
  },
  rowTitleWrap: {
    flex: 1,
    minWidth: 0,
  },
  rowTitleInner: {
    alignItems: "center",
    flexDirection: "row",
    gap: 6,
    minWidth: 0,
  },
  rowTitle: {
    color: Colors.blackColor,
    flex: 1,
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
  },
  rowUpdatedText: {
    color: Colors.grayColor,
    flexShrink: 1,
    fontSize: 10,
    fontStyle: "italic",
    lineHeight: 14,
  },
  nutritionSummaryRow: {
    alignItems: "flex-end",
    flexDirection: "row",
    gap: 8,
    justifyContent: "space-between",
    marginTop: 2,
  },
  nutritionSummaryLeft: {
    flex: 1,
    minWidth: 0,
  },
  kcalValue: {
    color: Colors.darkgrayColor,
    fontSize: 13,
    fontWeight: "600",
    lineHeight: 16,
  },
  kcalUnit: {
    color: Colors.grayColor,
    fontSize: 11,
    fontWeight: "400",
  },
  macroRow: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "flex-end",
  },
  macroValue: {
    color: Colors.grayColor,
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 0.2,
    lineHeight: 16,
  },
  macroNum: {
    color: Colors.grayColor,
    fontSize: 11,
    fontWeight: "400",
  },
  rowActions: {
    alignItems: "center",
    flexDirection: "row",
    gap: 2,
    marginLeft: 8,
  },
  cardIconButton: {
    alignItems: "center",
    borderRadius: 10,
    height: 28,
    justifyContent: "center",
    width: 28,
  },
  cardFooter: {
    alignItems: "center",
    borderTopColor: Colors.borderColor,
    borderTopWidth: 0.5,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  statusActionTouchable: {
    marginLeft: "auto",
  },
  statusActionTouchableDisabled: {
    opacity: 0.48,
  },
  statusActionButton: {
    alignItems: "center",
    borderRadius: 8,
    flexDirection: "row",
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  statusActionButtonHovered: {
    backgroundColor: Colors.hoveredGray,
  },
  statusActionButtonText: {
    color: Colors.darkgrayColor,
    fontSize: 11,
    fontWeight: "500",
    letterSpacing: 0.2,
  },
  menuItemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  menuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  menuItemText: {
    fontSize: 12,
    color: Colors.blackColor,
    fontWeight: "500",
  },
  menuItemDanger: {
    color: Colors.redColor,
  },
});

const ClientNutritionPlanRowCardBody = memo(function ClientNutritionPlanRowCardBody({
  plan,
  conflictSummary = null,
  effectiveStatus = null,
  menuItems,
  onMovePlan,
  onMenuAction,
  dragDisabled = false,
  isMovePending = false,
  isDropZoneActive = false,
  isHovered = false,
  isMenuOpen = false,
  isMenuTriggerHovered = false,
  isStatusActionHovered = false,
  setIsHovered,
  setIsMenuOpen,
  setIsMenuTriggerHovered,
  setIsStatusActionHovered,
}) {
  const { i18n, t } = useTranslation();
  const { isMobile } = useDevice();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const displayName = getNutritionPlanDisplayName(plan, language);
  const updatedDateLabel = formatNutritionPlanUpdatedDate(plan?.updatedAt, language);
  const updatedDateMetaLabel = updatedDateLabel
    ? t("nutritionPlanUpdatedDate-string", {
        defaultValue: "Updated {{date}}",
        date: updatedDateLabel,
      })
    : "";
  const totals = resolveClientNutritionPlanRowTotals(plan);
  const macroValues = {
    protein: totals?.protein,
    carbs: totals?.carbs,
    fats: totals?.fats,
  };
  const conflictTooltip = getNutritionConflictDescription(conflictSummary);
  const isActivePlan =
    String(effectiveStatus ?? plan?.status ?? "").trim().toLowerCase() ===
    CLIENT_NUTRITION_STATUS_ACTIVE;
  const statusActionTargetStatus = isActivePlan
    ? CLIENT_NUTRITION_STATUS_ARCHIVED
    : CLIENT_NUTRITION_STATUS_ACTIVE;
  const statusActionLabel = isActivePlan
    ? t("nutritionPlanAssignmentsArchiveAction-string", "Archive")
    : t("makeActive-string", "Set as active");
  const statusActionIconName = isActivePlan
    ? "archive-outline"
    : "checkmark-circle-outline";
  const isStatusActionDisabled =
    dragDisabled || isMovePending || typeof onMovePlan !== "function";

  return (
    <View
      style={[
        styles.rowCard,
        isHovered ? styles.rowCardHovered : null,
        isDropZoneActive ? styles.rowCardDropZoneActive : null,
      ]}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <View style={styles.rowContent}>
        <View style={styles.rowHeader}>
          <View style={styles.rowTitleWrap}>
            <View style={styles.rowTitleInner}>
              {isActivePlan ? <ActivePulseIndicator /> : null}
              <NutritionConflictWarningIcon
                tooltip={conflictTooltip}
                size={14}
              />
              <Text numberOfLines={1} style={styles.rowTitle}>
                {displayName}
              </Text>
            </View>
          </View>

          <View style={styles.rowActions}>
            <FloatingMenu
              placement="bottom-end"
              width={230}
              items={menuItems}
              open={isMenuOpen}
              onOpenChange={setIsMenuOpen}
              triggerContainerStyle={styles.cardIconButton}
              trigger={
                <View
                  style={{
                    opacity: isMenuTriggerHovered ? 1 : 0.75,
                    transition: "opacity 150ms ease",
                  }}
                  onMouseEnter={() => !isMobile && setIsMenuTriggerHovered(true)}
                  onMouseLeave={() => setIsMenuTriggerHovered(false)}
                >
                  <Entypo
                    name="dots-three-horizontal"
                    size={16}
                    color={isMenuTriggerHovered ? Colors.darkgrayColor : Colors.grayColor}
                  />
                </View>
              }
              renderItem={({ item, close }) => (
                <HoverableTouchableOpacity
                  style={styles.menuItemRow}
                  hoverStyle={styles.menuItemHover}
                  onPress={(event) => {
                    event?.stopPropagation?.();
                    onMenuAction?.(plan, item.key);
                    close();
                  }}
                  title={item.label}
                >
                  {item?.icon?.type === "ionicons" ? (
                    <Ionicons
                      name={item.icon.name}
                      size={14}
                      color={item.isDanger ? Colors.redColor : Colors.darkgrayColor}
                    />
                  ) : null}
                  <Text
                    style={[
                      styles.menuItemText,
                      item.isDanger ? styles.menuItemDanger : null,
                    ]}
                  >
                    {item.label}
                  </Text>
                </HoverableTouchableOpacity>
              )}
            />
          </View>
        </View>

        <View style={styles.nutritionSummaryRow}>
          <View style={styles.nutritionSummaryLeft}>
            <Text style={styles.kcalValue}>
              {formatCalorieValue(totals?.calories)}
              <Text style={styles.kcalUnit}> kcal</Text>
            </Text>
          </View>
          <View style={styles.macroRow}>
            {UI_MACRO_DISPLAY_ORDER.map((key) => (
              <Text key={key} style={styles.macroValue}>
                {getUiMacroShortLabel(key, language)}{" "}
                <Text style={styles.macroNum}>{formatMacroValue(macroValues[key])}g</Text>
              </Text>
            ))}
          </View>
        </View>
      </View>

      <View style={styles.cardFooter}>
        {updatedDateMetaLabel ? (
          <Text
            ellipsizeMode="head"
            numberOfLines={1}
            style={styles.rowUpdatedText}
          >
            {updatedDateMetaLabel}
          </Text>
        ) : null}
        <TouchableOpacity
          activeOpacity={isStatusActionDisabled ? 1 : 0.82}
          disabled={isStatusActionDisabled}
          onMouseEnter={() =>
            !isMobile && !isStatusActionDisabled && setIsStatusActionHovered(true)
          }
          onMouseLeave={() => setIsStatusActionHovered(false)}
          onPress={(event) => {
            event?.stopPropagation?.();
            onMovePlan?.(plan, statusActionTargetStatus);
          }}
          style={[
            styles.statusActionTouchable,
            isStatusActionDisabled ? styles.statusActionTouchableDisabled : null,
          ]}
        >
          <View
            style={[
              styles.statusActionButton,
              isStatusActionHovered && !isStatusActionDisabled
                ? styles.statusActionButtonHovered
                : null,
            ]}
          >
            <Ionicons
              name={statusActionIconName}
              size={13}
              color={Colors.darkgrayColor}
            />
            <Text style={styles.statusActionButtonText}>{statusActionLabel}</Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
});

function ClientNutritionPlanRowCard({
  folderKey,
  plan,
  conflictSummary = null,
  effectiveStatus = null,
  menuItems,
  onPress,
  onMovePlan,
  onMenuAction,
  dragDisabled = false,
  isMovePending = false,
  suppressPress = false,
  isDropZoneActive = false,
}) {
  const { screenSize } = useScreenDimensions();
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuTriggerHovered, setIsMenuTriggerHovered] = useState(false);
  const [isStatusActionHovered, setIsStatusActionHovered] = useState(false);
  const rowPressStartedWithMenuOpenRef = useRef(false);
  const planId = String(plan?.id ?? "").trim();
  const dragData = useMemo(
    () => ({
      type: "nutritionPlanRow",
      planId,
      sourceFolderKey: folderKey,
    }),
    [folderKey, planId],
  );
  const isDragDisabled = dragDisabled || isMovePending;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `client-nutrition-plan-row-${planId}`,
    data: dragData,
    disabled: isDragDisabled,
  });
  const dragActivatorProps = useMemo(
    () => (isDragDisabled ? {} : { ...attributes, ...listeners }),
    [attributes, isDragDisabled, listeners],
  );

  return (
    <View
      ref={setNodeRef}
      style={[
        getCardWidthStyle(screenSize),
        isDragging ? styles.cardWrapDragging : null,
      ]}
    >
      <TouchableOpacity
        activeOpacity={0.92}
        focusable
        onPressIn={() => {
          rowPressStartedWithMenuOpenRef.current = Boolean(isMenuOpen);
        }}
        onPress={() => {
          if (suppressPress || isDragging) {
            return;
          }

          if (rowPressStartedWithMenuOpenRef.current) {
            rowPressStartedWithMenuOpenRef.current = false;
            return;
          }

          onPress?.(plan);
        }}
        style={styles.rowTouchable}
        {...dragActivatorProps}
      >
        <ClientNutritionPlanRowCardBody
          plan={plan}
          conflictSummary={conflictSummary}
          effectiveStatus={effectiveStatus}
          menuItems={menuItems}
          onMovePlan={onMovePlan}
          onMenuAction={onMenuAction}
          dragDisabled={dragDisabled}
          isMovePending={isMovePending}
          isDropZoneActive={isDropZoneActive && !isDragging}
          isHovered={isHovered}
          isMenuOpen={isMenuOpen}
          isMenuTriggerHovered={isMenuTriggerHovered}
          isStatusActionHovered={isStatusActionHovered}
          setIsHovered={setIsHovered}
          setIsMenuOpen={setIsMenuOpen}
          setIsMenuTriggerHovered={setIsMenuTriggerHovered}
          setIsStatusActionHovered={setIsStatusActionHovered}
        />
      </TouchableOpacity>
    </View>
  );
}

function areClientNutritionPlanRowCardPropsEqual(previousProps, nextProps) {
  return (
    previousProps.folderKey === nextProps.folderKey &&
    previousProps.plan === nextProps.plan &&
    previousProps.conflictSummary === nextProps.conflictSummary &&
    previousProps.effectiveStatus === nextProps.effectiveStatus &&
    previousProps.menuItems === nextProps.menuItems &&
    previousProps.onPress === nextProps.onPress &&
    previousProps.onMovePlan === nextProps.onMovePlan &&
    previousProps.onMenuAction === nextProps.onMenuAction &&
    previousProps.dragDisabled === nextProps.dragDisabled &&
    previousProps.isMovePending === nextProps.isMovePending &&
    previousProps.suppressPress === nextProps.suppressPress &&
    previousProps.isDropZoneActive === nextProps.isDropZoneActive
  );
}

export default memo(
  ClientNutritionPlanRowCard,
  areClientNutritionPlanRowCardPropsEqual,
);
